import { useCallback } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  reserveOrder,
  confirmOrder,
  getOrderDetail,
  getOrdersForListing,
  getMyOrders,
  cancelOrder,
  getCancelPreview,
  verifyOrder,
  listerConfirmOrder,
  onMyWayOrder,
  nudgeOrder,
  denyHandoff,
  patchOrderStatus,
  markAllOrdersReady,
  startDropoffRun,
  createOrderReview,
} from '../services/orderService';
import type { ListingOrderSummary } from '../services/orderService';
import type { CreateOrderReviewRequest } from '../services/orderService';
import type { ReserveOrderRequest, ConfirmOrderRequest, OrderStatus, OrderDetail, MyOrderSummary } from '../types/order';
import { useLocationStore } from '../store/useLocationStore';

export function useReserveOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReserveOrderRequest) => reserveOrder(body),
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['listing', variables.listingId] });
      // A hold takes the portion out of circulation immediately, so the feed
      // cards behind the detail page are wrong the moment the reserve lands.
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings-map'] });
    },
  });
}

export function useConfirmOrder() {
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ConfirmOrderRequest }) =>
      confirmOrder(id, body),
  });
}

/**
 * Statuses we keep polling while on the order-in-progress page. The pre-payment
 * states resolve out-of-band (Stripe webhook), and the handoff state
 * (ready_to_pickup) can flip to `completed` the moment the OTHER
 * party confirms their half of the handshake — with no notification to this
 * client when the orderer confirmed first — so we poll through them too.
 */
const POLLING_STATUSES: OrderStatus[] = [
  'held',
  'confirmed',
  'paid',
  'ready_to_pickup',
];

/**
 * Handoff states where the completion handshake plays out. While any order for a
 * listing sits here, the lister's card language depends on the orderer's confirm
 * — which arrives with no push — so the lister views poll to stay live.
 */
const HANDOFF_STATUSES = new Set(['ready_to_pickup']);

function handoffRefetchInterval(
  data: ListingOrderSummary[] | undefined,
): number | false {
  if (!data) return false;
  return data.some((o) => HANDOFF_STATUSES.has(o.status)) ? 4000 : false;
}

export function useListingOrders(listingId: string | undefined) {
  return useQuery({
    queryKey: ['listing-orders', listingId],
    queryFn: () => getOrdersForListing(listingId!),
    enabled: !!listingId,
    refetchInterval: (query) => handoffRefetchInterval(query.state.data),
  });
}

/**
 * Fetches orders for several listings at once (one query per listing, run in
 * parallel). Keys match `useListingOrders` exactly so the WS `order_*`
 * invalidation of `['listing-orders']` refreshes both the per-listing view and
 * this aggregate view. Returns the raw query results in the same order as
 * `listingIds`; callers zip them back to their listing.
 */
export function useListingsOrders(listingIds: string[]) {
  return useQueries({
    queries: listingIds.map((id) => ({
      queryKey: ['listing-orders', id],
      queryFn: () => getOrdersForListing(id),
      refetchInterval: (query: { state: { data?: ListingOrderSummary[] } }) =>
        handoffRefetchInterval(query.state.data),
    })),
  });
}

// Buyer-history page size. 20 matches the local-listings feed default.
const MY_ORDERS_PAGE_SIZE = 20;

// Matches the lister-side handoff poll (handoffRefetchInterval).
const MY_ORDERS_POLL_MS = 4000;

/**
 * The caller's own placed orders (buyer-side history), cursor-paginated for
 * infinite scroll. `includeAll` maps to the server `include` param: false
 * (default) hides never-paid orders, true returns everything.
 *
 * Key is `['my-orders', 'all' | 'paid']` — the filter variant is part of the
 * key so flipping the toggle refetches cleanly. The WS `order_*` handler
 * invalidates the `['my-orders']` prefix, so both variants refresh live.
 *
 * `pollWhile`: poll every MY_ORDERS_POLL_MS while it returns true for the loaded
 * rows. It's an observer option, so only the caller that passes it polls (the
 * other subscribers to the same key are unaffected). OrderConfirmGate uses it so
 * the app-wide block clears even when the socket missed the event that ended it.
 */
export function useMyOrders(
  includeAll: boolean,
  opts: { pollWhile?: (orders: MyOrderSummary[]) => boolean } = {},
) {
  const include = includeAll ? 'all' : 'paid';
  const { pollWhile } = opts;
  return useInfiniteQuery({
    queryKey: ['my-orders', include],
    queryFn: ({ pageParam }) =>
      getMyOrders({
        fetchSize: MY_ORDERS_PAGE_SIZE,
        lastOrderId: pageParam,
        include,
      }),
    initialPageParam: undefined as string | undefined,
    // A full page implies there may be more; cursor is the last row's id. A
    // short (or empty) page means we've reached the end.
    getNextPageParam: (lastPage) =>
      lastPage.length === MY_ORDERS_PAGE_SIZE
        ? lastPage[lastPage.length - 1].orderId
        : undefined,
    refetchInterval: pollWhile
      ? (query) => (pollWhile(query.state.data?.pages.flat() ?? []) ? MY_ORDERS_POLL_MS : false)
      : undefined,
  });
}

/**
 * `quiet404`: a missing order skips the global error modal — for background
 * readers (activity cards) that handle "it's gone" themselves. A page opening
 * the order still gets the modal: it fetches with its own options.
 */
export function useOrderDetail(id: string | undefined, opts: { quiet404?: boolean } = {}) {
  const quiet404 = !!opts.quiet404;
  // Distance / drive-time to the lister are only trustworthy from device
  // geolocation — strip them at the data layer otherwise (mirrors the listing
  // feed gate) so OrderInProgressPage never renders a distance we can't stand
  // behind.
  const distanceAllowed = useLocationStore((s) => s.locationSource === 'device');
  const select = useCallback(
    (order: OrderDetail): OrderDetail =>
      distanceAllowed ? order : { ...order, distance: undefined, driveTime: undefined },
    [distanceAllowed],
  );
  return useQuery({
    queryKey: ['order', id],
    queryFn: () => getOrderDetail(id!, { quiet404 }),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status) return false;
      return POLLING_STATUSES.includes(status) ? 3000 : false;
    },
    select,
  });
}

/**
 * DELETE /orders/:id — releases a `held` order and, with it, the portion the
 * reserve had taken off the listing.
 *
 * `keepalive` is for the abandon-on-unload path in checkout: the browser kills
 * in-flight fetches during `beforeunload` unless the request is flagged
 * keepalive, which would otherwise strand the hold until the server-side
 * expiry worker reaps it.
 *
 * The invalidation list is deliberately wider than `['order', id]`: releasing a
 * hold changes the listing's remaining-portion count, which is rendered on the
 * detail page (`['listing']`), the home feed cards (`['local-listings']`), the
 * map pins (`['local-listings-map']`), the lister's order list
 * (`['listing-orders']`) and the buyer's own history (`['my-orders']`). Before
 * this, checkout called the `cancelOrder` service function directly and none of
 * these refreshed, so bailing out of checkout left every one of those surfaces
 * showing a portion that had already been given back.
 */
export function useCancelOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, keepalive }: { id: string; keepalive?: boolean }) =>
      cancelOrder(id, keepalive ? { keepalive: true } : undefined),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['order', id] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
      queryClient.invalidateQueries({ queryKey: ['listing-orders'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings-map'] });
      queryClient.invalidateQueries({ queryKey: ['my-orders'] });
    },
  });
}

/**
 * Fetches the server-computed cancellation fee/refund preview for an order.
 * Pass `enabled` to gate the request (typically tied to the cancel modal
 * being open). `staleTime: 0` so reopening the modal after a status change
 * always shows a fresh number.
 *
 * Key is intentionally NOT nested under ['order', id]: the cancel mutation
 * invalidates ['order', id] on success, and a cascaded refetch here against
 * the now-canceled order would hit the preview's `ErrBadStatus` guard —
 * which this codebase maps to HTTP 403, surfacing a spurious popup despite
 * the cancellation having succeeded.
 */
export function useCancelPreview(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['cancel-preview', id],
    queryFn: () => getCancelPreview(id!),
    enabled: !!id && enabled,
    staleTime: 0,
  });
}

/**
 * Refetches what the orderer's handoff UI renders from: the order detail (order
 * page, activity card) and the buyer-history rows (OrderConfirmGate). Returned so
 * the verify / deny mutations can await it. While a mutation's `onSuccess` /
 * `onError` promise is pending the mutation stays `isPending`, which keeps the
 * button disabled until the screen has caught up with the server. Without the
 * await, the button re-armed the moment the POST returned, and a second tap
 * landed on an order the first tap had already completed (403).
 */
function refetchOrdererHandoffViews(queryClient: QueryClient, id: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['order', id] }),
    queryClient.invalidateQueries({ queryKey: ['my-orders'] }),
  ]);
}

/**
 * The BE's 403 for "this order is no longer at ready_to_pickup". The client was
 * acting on a stale order (the chef canceled or declared a no-show while the
 * socket missed it, or an earlier tap already completed it). apiService
 * suppresses the global modal for this case on verify / deny-handoff; the hooks
 * refetch instead so the stale UI drops.
 */
function isStaleHandoffError(err: unknown): boolean {
  const apiErr = err as Error & { status?: number; errorCode?: string };
  return apiErr?.status === 403 && apiErr.errorCode === 'STATUS_INVALID';
}

export function useVerifyOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, code }: { id: string; code: number }) =>
      verifyOrder(id, code),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
      queryClient.invalidateQueries({ queryKey: ['listing-orders'] });
      // Buyer-history rows carry the handshake flags too, and OrderConfirmGate
      // keys its app-wide block off them, so they're in the awaited set: the
      // gate has to drop before its button re-arms.
      return refetchOrdererHandoffViews(queryClient, id);
    },
    onError: (err, { id }) => {
      if (isStaleHandoffError(err)) return refetchOrdererHandoffViews(queryClient, id);
    },
  });
}

/**
 * The lister's half of the completion handshake. Completes the order when the
 * orderer has already confirmed; otherwise records the lister's confirm and
 * pings the orderer once. Invalidates the same keys as verify so both the
 * per-listing and aggregate lister views refresh.
 */
export function useListerConfirmOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => listerConfirmOrder(id),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['order', id] });
      queryClient.invalidateQueries({ queryKey: ['listing-orders'] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });
}

/**
 * The orderer's "On My Way" signal for a ready_to_pickup order. Unlocks the
 * verification code (the backend withholds it until now) and notifies the
 * lister. Invalidates the same keys as verify so the orderer's card flips from
 * the button to the pickup ball and the lister's views pick up the indicator.
 */
export function useOnMyWay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => onMyWayOrder(id),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['order', id] });
      queryClient.invalidateQueries({ queryKey: ['listing-orders'] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });
}

/**
 * The lister's one-shot "come get it" nudge to the eater. Sends a push only — it
 * doesn't advance the order or spend the lister's handoff confirm. Invalidates
 * the same keys as the confirm so the button flips to its spent state.
 */
export function useNudgeOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => nudgeOrder(id),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['order', id] });
      queryClient.invalidateQueries({ queryKey: ['listing-orders'] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });
}

/**
 * The orderer's "No, I did not get my dish" exit from OrderConfirmGate. Undoes
 * the lister's Handed Off and notifies them. Invalidates `['my-orders']` along
 * with the verify keys: the gate reads the buyer-history rows, so without it the
 * gate would stay up after the denial that was supposed to clear it. Awaited for
 * the same reason as verify (see refetchOrdererHandoffViews).
 */
export function useDenyHandoff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => denyHandoff(id),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['listing-orders'] });
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
      return refetchOrdererHandoffViews(queryClient, id);
    },
    onError: (err, { id }) => {
      if (isStaleHandoffError(err)) return refetchOrdererHandoffViews(queryClient, id);
    },
  });
}

export function usePatchOrderStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      rejectionReason,
    }: {
      id: string;
      status: string;
      rejectionReason?: string;
    }) => patchOrderStatus(id, status, rejectionReason),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['order', id] });
      queryClient.invalidateQueries({ queryKey: ['listing-orders'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
      // This is the chef's no-show / cancel exit, and both change the listing's
      // `activeOrderCount` — which the home-feed lister panel keys its row
      // visibility off. Without this the panel kept a row for an order the chef
      // had just resolved. Matches useListerConfirmOrder, the other exit.
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      // The post-payment cancel is the other way a portion goes back on sale
      // (useCancelOrder covers the pre-payment one), so the feed cards and map
      // pins move the moment this lands. OrderInProgressPage cancels through
      // here and then redirects straight to the home feed, so without these the
      // user arrives looking at the count from before their own cancel. The WS
      // `order_*` echo invalidates the same keys, but the events socket has no
      // replay — a cancel sent while it's mid-reconnect is missed entirely, so
      // this is the path that has to be complete on its own.
      queryClient.invalidateQueries({ queryKey: ['local-listings'] });
      queryClient.invalidateQueries({ queryKey: ['local-listings-map'] });
      // ...and the buyer's own history, where the canceled row lives.
      queryClient.invalidateQueries({ queryKey: ['my-orders'] });
    },
  });
}

/**
 * Arms/disarms auto-ready for a listing and (when arming) flips all currently
 * accepted pickup orders to ready in one call. Invalidates the same keys as
 * usePatchOrderStatus so the per-listing and aggregate order views, plus the
 * listing itself (which now carries `autoReadyEnabled`), refresh.
 */
export function useMarkAllOrdersReady() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listingId, enabled }: { listingId: string; enabled: boolean }) =>
      markAllOrdersReady(listingId, enabled),
    onSuccess: (_data, { listingId }) => {
      queryClient.invalidateQueries({ queryKey: ['listing-orders', listingId] });
      queryClient.invalidateQueries({ queryKey: ['listing', listingId] });
    },
  });
}

/**
 * Starts the chef's drop-off run for a listing: every accepted drop-off order
 * goes out for delivery at once. Invalidates the per-order and per-listing order
 * views so each card flips to its out-for-delivery state.
 */
export function useStartDropoffRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listingId }: { listingId: string }) => startDropoffRun(listingId),
    onSuccess: (_data, { listingId }) => {
      queryClient.invalidateQueries({ queryKey: ['listing-orders', listingId] });
      queryClient.invalidateQueries({ queryKey: ['listing', listingId] });
      queryClient.invalidateQueries({ queryKey: ['order'] });
    },
  });
}

export function useCreateOrderReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, body }: { orderId: string; body: CreateOrderReviewRequest }) =>
      createOrderReview(orderId, body),
    onSuccess: (_data, { orderId }) => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['listing-orders'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });
}
